import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  InputAdornment,
  Divider,
  CircularProgress,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router-dom';
import useNotification from '../hooks/useNotification';
import userService from '../services/userService';
import roleService from '../services/roleService';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import '../styles/phone-input-custom.css';
import countriesData from '../assets/countryData';
import { PageTitleContainer } from '../components/common';

/**
 * CreateUser Component
 * A form to create new users in the system with a layout similar to CreateProject
 */
const CreateUser = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  // Refs for height calculation
  const contentRef = useRef(null);
  const mainHeaderRef = useRef(null);
  const titleHeaderRef = useRef(null);

  // State for content height
  const [contentHeight, setContentHeight] = useState('calc(100vh - 130px)');

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Form state
  const [formData, setFormData] = useState({
    // User account info
    firstName: '',
    lastName: '',
    email: '',
    country: 'IN', // Default to India
    password: '',
    confirmPassword: '',
    userType: '',

    // Profile info
    contactNumber: '91', // Default country code for India
    skypeDetails: '',
    workEmail: '',
    gmailForSharing: '',
    personalEmail: '',
    microsoftEmail: '',
    gender: '',
    callName: ''
  });

  // Validation errors
  const [errors, setErrors] = useState({});
  
  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Countries from imported data
  const countries = countriesData;

  // User types/roles (will be fetched from API)
  const [userTypes, setUserTypes] = useState([
    { value: 'admin', label: 'Administrator', description: 'Full access to all features and user management' },
    { value: 'user', label: 'Regular User', description: 'Can access assigned projects and features' },
    { value: 'guest', label: 'Guest', description: 'Limited access to view specific content' }
  ]);

  // Calculate content height based on window size and header heights
  useLayoutEffect(() => {
    const updateContentHeight = () => {
      // Default header heights (fallback values if refs aren't available)
      const mainHeaderHeight = 73;  // Main app header
      const titleHeaderHeight = 80; // Create project header
      const extraPadding = 69;      // Additional padding for spacing

      const totalHeaderHeight = mainHeaderHeight + titleHeaderHeight + extraPadding;
      setContentHeight(`calc(100vh - ${totalHeaderHeight}px)`);
    };

    // Initial calculation
    updateContentHeight();

    // Re-calculate on window resize
    window.addEventListener('resize', updateContentHeight);

    return () => {
      window.removeEventListener('resize', updateContentHeight);
    };
  }, []);

  // Fetch roles on component mount
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await roleService.getAllRoles();
        const roles = response?.roles || [];
        if (roles.length > 0) {
          setUserTypes(roles.map(r => ({
            value: r.role_id || r.name,
            label: r.name,
            description: r.description || ''
          })));
        }
      } catch (error) {
        console.error('Error fetching roles:', error);
        showNotification({
          message: 'Failed to load user roles',
          type: 'error'
        });
      }
    };

    fetchRoles();
  }, [showNotification]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    // Clear error when field is changed
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle phone number change
  const handlePhoneChange = (value) => {
    setFormData(prev => ({ 
      ...prev, 
      contactNumber: value 
    }));
  };

  // Toggle password visibility
  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  // Toggle confirm password visibility
  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Validate required fields
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.country) {
      newErrors.country = 'Country is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.userType) {
      newErrors.userType = 'User type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      // Prepare user data
      const userData = {
        name: `${formData.firstName} ${formData.lastName}`,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        country: formData.country,
        role: formData.userType, // Selected role_id slug — backend resolves everything

        // Profile info
        profile: {
          contactNumber: formData.contactNumber,
          skypeDetails: formData.skypeDetails,
          workEmail: formData.workEmail || formData.email, // Use primary email as fallback
          gmailForSharing: formData.gmailForSharing,
          personalEmail: formData.personalEmail,
          microsoftEmail: formData.microsoftEmail,
          gender: formData.gender,
          callName: formData.callName || formData.firstName // Use first name as fallback
        }
      };

      // Call user creation API
      await userService.createUser(userData);

      // Show success notification
      showNotification({
        message: 'User created successfully!',
        type: 'success'
      });

      // Redirect to users list
      navigate('/users');
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to create user. Please try again.';

      // Show error in both notification and inline error
      showNotification({
        message: errorMessage,
        type: 'error'
      });
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/users');
  };

  // Common input field styling
  const inputFieldStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px', // Update border radius to 4px
      height: '45px',
      transition: 'all 0.2s',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
        borderWidth: '1px',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
        borderWidth: '1px',
      },
      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: '#f44336',
      }
    },
    '& .MuiInputBase-input': {
      fontSize: '16px',
      padding: '0 20px',  // This adds 20px padding to both left and right sides
      height: '45px',
      boxSizing: 'border-box'
    },
    '& .MuiInputBase-input::placeholder': {
      fontSize: '15px',
      opacity: 0.7
    },
    '& .MuiInputAdornment-root': {
      marginLeft: '8px', // Add extra padding to icons
      marginRight: '8px'
    },
    '& .MuiFormHelperText-root': {
      marginLeft: '4px',
      fontSize: '12px'
    }
  };

  // Select field styling
  const selectFieldStyle = {
    height: '45px',
    borderRadius: '4px', // Update border radius to 4px
    '& .MuiSelect-select': {
      height: '45px',
      padding: '0 20px', // Adding equal padding to both left and right sides
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between', // This creates space between content and dropdown arrow
      '&::after': {
        content: '""',
        width: '24px' // This reserves space for the dropdown icon
      }
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'primary.main',
      borderWidth: '1px',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'primary.main',
      borderWidth: '1px',
    },
    // Styling for the dropdown icon
    '& .MuiSelect-icon': {
      color: 'rgba(0, 0, 0, 0.54)',
      fontSize: '1.25rem',
      right: '7px'
    }
  };

  // Render appropriate tab content
  const renderTabContent = () => {
    if (tabValue === 0) {
      return (
        <Box sx={{ p: 5 }}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ mb: 1, display: 'block', fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                First name <span style={{ color: '#F89939' }}>*</span>
              </Typography>
              <TextField
                required
                id="firstName"
                name="firstName"
                variant="outlined"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter first name"
                fullWidth
                error={!!errors.firstName}
                helperText={errors.firstName}
                InputProps={{}}
                sx={inputFieldStyle}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ mb: 1, display: 'block', fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                Last name <span style={{ color: '#F89939' }}>*</span>
              </Typography>
              <TextField
                required
                id="lastName"
                name="lastName"
                variant="outlined"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter last name"
                fullWidth
                error={!!errors.lastName}
                helperText={errors.lastName}
                InputProps={{}}
                sx={inputFieldStyle}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ mb: 1, display: 'block', fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                Email <span style={{ color: '#F89939' }}>*</span>
              </Typography>
              <TextField
                required
                id="email"
                name="email"
                type="email"
                variant="outlined"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
                fullWidth
                error={!!errors.email}
                helperText={errors.email}
                autoComplete="off"
                InputProps={{}}
                sx={inputFieldStyle}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ mb: 1, display: 'block', fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                Password <span style={{ color: '#F89939' }}>*</span>
              </Typography>
              <TextField
                required
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter password"
                fullWidth
                error={!!errors.password}
                helperText={errors.password}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePassword}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={inputFieldStyle}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ mb: 1, display: 'block', fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                Confirm password <span style={{ color: '#F89939' }}>*</span>
              </Typography>
              <TextField
                required
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                variant="outlined"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm password"
                fullWidth
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleToggleConfirmPassword}
                        edge="end"
                        size="small"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={inputFieldStyle}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ mb: 1, display: 'block', fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                User type <span style={{ color: '#F89939' }}>*</span>
              </Typography>
              <FormControl 
                fullWidth
                error={!!errors.userType}
              >
                <Select
                  id="userType"
                  name="userType"
                  value={formData.userType}
                  onChange={handleChange}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      }
                    }
                  }}
                  IconComponent={(props) => (
                    <Box
                      component="svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      {...props}
                      sx={{
                        width: 16,
                        height: 16,
                        right: 16,
                        position: 'absolute',
                        pointerEvents: 'none',
                        color: 'rgba(0, 0, 0, 0.54)',
                      }}
                    >
                      <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </Box>
                  )}
                  sx={{
                    ...selectFieldStyle,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: errors.userType ? '#f44336' : 'rgba(0, 0, 0, 0.23)',
                    },
                  }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <Typography sx={{ color: 'text.secondary', fontSize: '15px' }}>Select user type</Typography>;
                    }
                    const selectedType = userTypes.find(type => type.value === selected);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box
                          sx={{
                            display: 'inline-block',
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: selected === 'admin' ? '#4DBCF3' : selected === 'guest' ? '#F89939' : selected === 'customer' ? '#9B59B6' : '#66CC33',
                            mr: 1,
                          }}
                        />
                        <Typography sx={{ flex: 1 }}>
                          {selectedType ? selectedType.label : selected}
                        </Typography>
                      </Box>
                    );
                  }}
                >
                  {userTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              display: 'inline-block',
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: type.value === 'admin' ? '#4DBCF3' : type.value === 'guest' ? '#F89939' : type.value === 'customer' ? '#9B59B6' : '#66CC33',
                              mr: 1,
                            }}
                          />
                          {type.label}
                        </Box>
                        {type.description && (
                          <InfoIcon fontSize="small" sx={{ color: '#94a3b8', ml: 1 }} />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.userType && <FormHelperText>{errors.userType}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ mb: 1, display: 'block', fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                Country <span style={{ color: '#F89939' }}>*</span>
              </Typography>
              <FormControl
                fullWidth
                error={!!errors.country}
              >
                <Select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      }
                    }
                  }}
                  IconComponent={(props) => (
                    <Box
                      component="svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      {...props}
                      sx={{
                        width: 16,
                        height: 16,
                        right: 16,
                        position: 'absolute',
                        pointerEvents: 'none',
                        color: 'rgba(0, 0, 0, 0.54)',
                      }}
                    >
                      <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </Box>
                  )}
                  sx={{
                    ...selectFieldStyle,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: errors.country ? '#f44336' : 'rgba(0, 0, 0, 0.23)',
                    },
                  }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <Typography sx={{ color: 'text.secondary', fontSize: '15px' }}>Select country</Typography>;
                    }
                    const selectedCountry = countries.find(c => c.code === selected);
                    if (!selectedCountry) return selected;

                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box
                          component="img"
                          src={selectedCountry.flag}
                          alt={selectedCountry.name}
                          sx={{ height: 18, mr: 1 }}
                        />
                        <Typography sx={{ flex: 1 }}>
                          {selectedCountry.name}
                        </Typography>
                      </Box>
                    );
                  }}
                >
                  {countries.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box 
                          component="img"
                          src={country.flag}
                          alt={country.name}
                          sx={{ height: 18, mr: 1 }}
                        />
                        {country.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.country && <FormHelperText>{errors.country}</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      );
    } else if (tabValue === 1) { // Profile tab
      return (
        <Box sx={{ p: 5 }}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ mb: 1, display: 'block', fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                Contact number
              </Typography>
              <Box sx={{
                '& .react-tel-input .form-control': {
                  width: '100%',
                  height: '45px',
                  fontSize: '16px',
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                  borderRadius: '4px', // Update border radius to 4px
                  paddingLeft: '58px', // Add padding for the flag
                  paddingRight: '20px', // Add right side padding to match other inputs
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    borderWidth: '1px',
                  },
                  '&:focus': {
                    borderColor: 'primary.main',
                    boxShadow: 'none',
                    borderWidth: '1px',
                  },
                  '&::placeholder': {
                    fontSize: '15px',
                    opacity: 0.7
                  }
                },
                '& .react-tel-input .flag-dropdown': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                  borderRadius: '4px 0 0 4px', // Update border radius to 4px
                  background: 'transparent',
                  '&:hover': {
                    borderColor: 'primary.main',
                    borderWidth: '1px',
                  }
                },
                '& .react-tel-input.focused .flag-dropdown': {
                  borderColor: 'primary.main',
                  borderWidth: '1px',
                }
              }}>
                <PhoneInput
                  country={'in'}
                  value={formData.contactNumber}
                  onChange={handlePhoneChange}
                  inputProps={{
                    name: 'contactNumber',
                    id: 'contactNumber',
                    placeholder: 'Enter phone number'
                  }}
                  containerStyle={{ width: '100%' }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ mb: 1, display: 'block', fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                Skype details
              </Typography>
              <TextField
                id="skypeDetails"
                name="skypeDetails"
                variant="outlined"
                value={formData.skypeDetails}
                onChange={handleChange}
                placeholder="Enter skype details"
                fullWidth
                sx={inputFieldStyle}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ mb: 1, display: 'block', fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                Work email
              </Typography>
              <TextField
                id="workEmail"
                name="workEmail"
                variant="outlined"
                value={formData.workEmail}
                onChange={handleChange}
                placeholder="Enter work email"
                fullWidth
                InputProps={{}}
                sx={inputFieldStyle}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ mb: 1, display: 'block', fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                Gmail for document sharing
              </Typography>
              <TextField
                id="gmailForSharing"
                name="gmailForSharing"
                variant="outlined"
                value={formData.gmailForSharing}
                onChange={handleChange}
                placeholder="Enter gmail address"
                fullWidth
                InputProps={{}}
                sx={inputFieldStyle}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ mb: 1, display: 'block', fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                Personal email
              </Typography>
              <TextField
                id="personalEmail"
                name="personalEmail"
                variant="outlined"
                value={formData.personalEmail}
                onChange={handleChange}
                placeholder="Enter personal email"
                fullWidth
                InputProps={{}}
                sx={inputFieldStyle}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ mb: 1, display: 'block', fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                Microsoft email
              </Typography>
              <TextField
                id="microsoftEmail"
                name="microsoftEmail"
                variant="outlined"
                value={formData.microsoftEmail}
                onChange={handleChange}
                placeholder="Enter microsoft email"
                fullWidth
                InputProps={{}}
                sx={inputFieldStyle}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ mb: 1, display: 'block', fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                Gender
              </Typography>
              <FormControl fullWidth>
                <Select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      }
                    }
                  }}
                  IconComponent={(props) => (
                    <Box
                      component="svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      {...props}
                      sx={{
                        width: 16,
                        height: 16,
                        right: 16,
                        position: 'absolute',
                        pointerEvents: 'none',
                        color: 'rgba(0, 0, 0, 0.54)',
                      }}
                    >
                      <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </Box>
                  )}
                  sx={selectFieldStyle}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <Typography sx={{ color: 'text.secondary', fontSize: '15px' }}>Select gender</Typography>;
                    }
                    return <Typography sx={{ flex: 1 }}>{selected}</Typography>;
                  }}
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                  <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ mb: 1, display: 'block', fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                Call name
              </Typography>
              <TextField
                id="callName"
                name="callName"
                variant="outlined"
                value={formData.callName}
                onChange={handleChange}
                placeholder="Enter preferred name"
                fullWidth
                sx={inputFieldStyle}
              />
            </Grid>
          </Grid>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box
      className="CreateUser-root"
      sx={{
        width: '100%',
        bgcolor: '#f5f6f7',
        minHeight: '100vh',
        pb: 4,
        px: 6,
        pt: 4,
        overflowX: 'hidden',
        backgroundImage: 'none', // Explicitly remove any background image
        '&::before': { display: 'none' },
        '&::after': { display: 'none' }
      }}>
      {/* Page Header */}
      <PageTitleContainer
        ref={titleHeaderRef}
        title="Create user"
        leftContent={
          <IconButton 
            onClick={handleBack}
            sx={{ 
              color: '#475569',
              mr: 2,
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        }
        sx={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          ml: 0,
          mr: 0,
          mb: 2,
          mt: 0,
          py: '16px' // Exact 16px padding top and bottom
        }}
        titleProps={{
          sx: { fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }
        }}
      />

      {/* Main Content */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', pt: 0, overflow: 'hidden', width: '100%' }}>
        <Paper
          elevation={0}
          ref={contentRef}
          sx={{
            borderRadius: '4px',
            width: '100%',
            height: contentHeight,
            bgcolor: 'white',
            overflowX: 'hidden',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            backgroundImage: 'none',
            '&::before': {
              display: 'none'
            },
            '&::after': {
              display: 'none'
            },
            m: "0px !important"
          }}
        >
          {error && (
            <Alert
              severity="error"
              sx={{
                mx: 3,
                mt: 3,
                borderRadius: 1.5,
                '& .MuiAlert-icon': {
                  color: '#f44336'
                }
              }}
            >
              {error}
            </Alert>
          )}

          {/* Tabs for user account and profile */}
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            sx={{
              borderBottom: '1px solid #e5e7eb',
              '& .MuiTabs-indicator': {
                backgroundColor: '#475569',
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                color: '#475569',
                '&.Mui-selected': {
                  color: '#475569',
                  fontWeight: 600,
                },
              },
              px: 3,
              pt: 1
            }}
          >
            <Tab label="User account" id="tab-0" />
            <Tab label="Profile" id="tab-1" />
          </Tabs>

          {/* Tab Content */}
          {renderTabContent()}

          {/* Submit button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0, px: 3, py: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                bgcolor: '#485869',
                color: 'white',
                '&:hover': {
                  bgcolor: '#3a4a59',
                },
                borderRadius: '4px', // Change button border radius to 4px as well for consistency
                px: 5,
                py: 0.5,
                minWidth: '120px',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
              }}
            >
              {isSubmitting ?
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} /> Creating...
                </Box> :
                'Save'
              }
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default CreateUser;